import { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';

interface UseAddEventGateParams {
	/** Opens the existing "Toegang tot evenement" choice (QR / code). */
	onOpenChoice: () => void;
}

/**
 * Soft login gate before adding an event.
 */
export function useAddEventGate({ onOpenChoice }: UseAddEventGateParams) {
	const isLoggedin = useAuthenticationStore((state) => state.isLoggedin);

	const requestAddEvent = useCallback(() => {
		if (isLoggedin === true) {
			onOpenChoice();
			return;
		}

		AlertHelper.showConfirmAlert({
			title: Translations.alertLoginRequiredTitle,
			message: Translations.alertLoginRequiredMessage,
			confirmText: Translations.buttonLogin,
			cancelText: Translations.buttonCancel,
			onConfirm: () => {
				useEventStore.getState().setPendingAddEventIntent(true);
				useNavigationStore.getState().navigate(Constants.screenLogin);
			},
		});
	}, [isLoggedin, onOpenChoice]);

	// Reacts only to real focus events: state is read non-reactively, so setting the intent + navigating away to
	// Login does not fire this effect prematurely (otherwise the just-set intent would be cleared again
	// immediately).
	useFocusEffect(
		useCallback(() => {
			const { pendingAddEventIntent, setPendingAddEventIntent } = useEventStore.getState();
			if (!pendingAddEventIntent) return;

			const loggedIn = useAuthenticationStore.getState().isLoggedin;
			if (loggedIn === true) {
				setPendingAddEventIntent(false);
				// Open the choice one frame after the focus render, so the alert does not botst met het einde van de
				// navigatie-transitie (iOS).
				requestAnimationFrame(() => onOpenChoice());
			} else if (loggedIn === false) {
				// Login cancelled (e.g. "Misschien later") -> drop the intent.
				setPendingAddEventIntent(false);
			}
		}, [onOpenChoice]),
	);

	return { requestAddEvent };
}
